import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    MenuItem,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import Swal from "sweetalert2";
import moment from "moment";
import { useReactToPrint } from "react-to-print";

import { auth, db } from "../firebase";
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    getDoc,
    doc,
    updateDoc,
    Timestamp,
} from "firebase/firestore";

export default function RoomPostingReport() {
    /* -------------------- TOAST -------------------- */
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
    });

    /* -------------------- STATES -------------------- */
    const printRef = useRef();

    const [propertyId, setPropertyId] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [payModes, setPayModes] = useState([]);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [type, setType] = useState("All");

    const [orderId, setOrderId] = useState(null);
    const [payType, setPayType] = useState("");

    /* -------------------- INIT -------------------- */
    useEffect(() => {
        const today = new Date().toISOString().split("T")[0];
        setFromDate(today);
        setToDate(today);
    }, []);

    /* -------------------- FETCH PROPERTY ID -------------------- */
    useEffect(() => {
        if (!auth.currentUser) return;

        const q = query(
            collection(db, "authroles"),
            where("uid", "==", auth.currentUser.uid)
        );

        const unsub = onSnapshot(q, (snap) => {
            if (!snap.empty) {
                setPropertyId(snap.docs[0].data().propertyId);
            }
        });

        return () => unsub();
    }, []);

    /* -------------------- FETCH PAY MODES -------------------- */
    useEffect(() => {
        if (!propertyId) return;

        const q = query(
            collection(db, "paymentmodes"),
            where("propertyId", "==", propertyId)
        );

        const unsub = onSnapshot(q, (snap) => {
            setPayModes(
                snap.docs.map((d) => ({
                    id: d.id,
                    ...d.data(),
                }))
            );
        });

        return () => unsub();
    }, [propertyId]);

    /* -------------------- GET REPORT -------------------- */
    const getReport = () => {
        if (!propertyId) {
            Toast.fire({ icon: "error", title: "Property not found" });
            return;
        }

        const fromTS = Timestamp.fromDate(
            new Date(fromDate + " 00:00:00")
        );

        const toTS = Timestamp.fromDate(
            new Date(toDate + " 23:59:59")
        );


        const q = query(
            collection(db, "orders"),
            where("propertyId", "==", propertyId),
            where("orderType", "==", "ROOM"),
            where("billdate", ">=", fromTS),
            where("billdate", "<=", toTS),
            orderBy("billdate", "desc"),
        );

        onSnapshot(q, async (snap) => {
            const rows = await Promise.all(
                snap.docs.map(async (d) => {
                    const order = d.data();
                    let reservation = null;

                    if (order.reservationId) {
                        const rSnap = await getDoc(
                            doc(db, "reservations", order.reservationId)
                        );
                        reservation = rSnap.exists() ? rSnap.data() : null;
                    }

                    console.log(reservation);

                    return {
                        id: d.id,
                        order,
                        reservation,
                    };
                })
            );

            setReportData(rows);
        });
    };

    /* -------------------- UPDATE PAYMENT -------------------- */
    const updateSettlement = async () => {
        if (!orderId || !payType) {
            Toast.fire({ icon: "error", title: "Select payment type" });
            return;
        }

        await updateDoc(doc(db, "orders", orderId), {
            paytype: payType,
            status: "Active",
            updatedAt: new Date(),
        });

        Toast.fire({
            icon: "success",
            title: "Room posting settled successfully",
        });

        setOrderId(null);
        setPayType("");
    };

    /* -------------------- PRINT -------------------- */
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
    });

    /* -------------------- UI -------------------- */
    return (
        <Box className=" p-4">
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Room Posting Report
                    </Typography>

                    <Grid className="row" spacing={2}>

                        <Grid className="col-sm-3" md={3}>
                            <TextField
                                type="date"
                                label="From Date"
                                fullWidth size="small"
                                InputLabelProps={{ shrink: true }}
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </Grid>

                        <Grid className="col-sm-3" md={3}>
                            <TextField
                                type="date"
                                label="To Date"
                                fullWidth size="small"
                                InputLabelProps={{ shrink: true }}
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </Grid>

                        <Grid className="col-sm-3" md={3} alignSelf="end">
                            <Button variant="contained" fullWidth onClick={getReport}>
                                Get Report
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* -------------------- TABLE -------------------- */}
            <Card sx={{ mt: 3 }}>
                <CardContent ref={printRef}>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sr</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Bill No</TableCell>
                                    <TableCell>Guest</TableCell>
                                    <TableCell>Rooms</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">Paid</TableCell>
                                    <TableCell align="right">Remaining</TableCell>
                                    {/* <TableCell>Action</TableCell> */}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {reportData.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            No Data Found
                                        </TableCell>
                                    </TableRow>
                                )}

                                {reportData.map((row, index) => {
                                    const total = Number(row.order.totalamount || 0);
                                    const paid = Number(row.order.paid || 0);
                                    const remaining = total - paid;

                                    return (
                                        <TableRow key={row.id}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>
                                                {row.order.billdate
                                                    ? moment(row.order.billdate.toDate()).format("DD/MM/YYYY")
                                                    : "-"}
                                            </TableCell>

                                            <TableCell>{row.order.billno}</TableCell>
                                            <TableCell>
                                                {row.reservation?.customers?.custname}
                                            </TableCell>
                                            <TableCell>
                                                {row.reservation?.rooms?.map((r, i) => (
                                                    <Chip
                                                        key={i}
                                                        label={r.roomno}
                                                        size="small"
                                                        sx={{ mr: 0.5 }}
                                                    />
                                                ))}
                                            </TableCell>
                                            <TableCell align="right">
                                                ₹ {total.toFixed(2)}
                                            </TableCell>
                                            <TableCell align="right">
                                                ₹ {paid.toFixed(2)}
                                            </TableCell>
                                            <TableCell align="right">
                                                ₹ {remaining.toFixed(2)}
                                            </TableCell>
                                            {/* <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="success"
                                                    onClick={() => setOrderId(row.id)}
                                                >
                                                    Settle
                                                </Button>
                                            </TableCell> */}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>

                <Box textAlign="center" pb={2}>
                    <Button onClick={handlePrint} variant="outlined">
                        Print Report
                    </Button>
                </Box>
            </Card>

            {/* -------------------- SETTLEMENT DIALOG -------------------- */}
            <Dialog open={Boolean(orderId)} onClose={() => setOrderId(null)} fullWidth>
                <DialogTitle>Payment Settlement</DialogTitle>

                <DialogContent>
                    <TextField
                        select
                        fullWidth
                        label="Payment Mode"
                        value={payType}
                        onChange={(e) => setPayType(e.target.value)}
                        margin="dense"
                    >
                        {payModes.map((p) => (
                            <MenuItem key={p.id} value={p.paymode}>
                                {p.paymode}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOrderId(null)}>Cancel</Button>
                    <Button variant="contained" onClick={updateSettlement}>
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
